import { Button, Panel, RsInput, Table } from '@/components/Bicester';
import Image from '@/components/Bicester/Image';
import { Cell, UserNameCell } from '@/components/Bicester/Table/Cells';
import { Bicester } from '@/constants';
import { DEFAULT_TABLE_VALUE } from '@/constants/config';
import useContentHeight from '@/hooks/useContentHeight';
import useTable from '@/hooks/useTable';
import { useReadyInfo } from '@/providers/ReadyProvider';
import { SERVICE_STATUS } from '@/services/constants';
import { fetchParkingList, fetchCheckIn, fetchRevokeIn } from '@/services/subscribe/parking';
import { useRequest } from 'ahooks';
import _ from 'lodash';
import React, { useState } from 'react';
import { Alert, IconButton } from 'rsuite';

interface ParkingForm {
  /**
   * 车牌号
   */
  content?: string;
  /**
   * 备注
   */
  remark?: string;
}

const Parking = () => {
  const [form, setForm] = useState<ParkingForm>({});

  const { profile } = useReadyInfo();

  const {
    run: getList,
    data: { total, content_list, current_page },
    loading,
  } = useRequest<any>(fetchParkingList, {
    initialData: DEFAULT_TABLE_VALUE,
  });

  const { onChange: handleTableChange, onSearch, onRefresh } = useTable(getList, form);

  return (
    <Panel
      title={`${Bicester[profile?.shopping_village]} | 代客泊车今日预约`}
      loading={loading}
      leftShadow
      style={{
        height: 'calc(100vh - var(--headerHeight) - var(--navBarHeight) - var(--toolBarHeight))',
      }}
      subTitle={`总计 ${total || 0} 条`}
      toolbar={
        <div className="flex justify-end items-center">
          <div className="text-[#999999] mr-[15px]">备注</div>
          <div className="w-[210px] h-[30px]">
            <RsInput
              value={form?.remark || ''}
              onChange={(remark) => setForm({ ...form, remark })}
              onPressEnter={onSearch}
            />
          </div>
          <div className="text-[#999999] mr-[15px] ml-[30px]">车牌号</div>
          <div className="w-[210px] h-[30px]">
            <RsInput
              value={form?.content || ''}
              onChange={(content) => setForm({ ...form, content })}
              onPressEnter={onSearch}
            />
          </div>
          <IconButton
            type="submit"
            className="inline-flex items-center w-[30px] ml-[10px]"
            onClick={onSearch}
          >
            <Image src="/resources/images/match.svg" />
          </IconButton>
        </div>
      }
    >
      <Table
        style={{
          height: useContentHeight({
            withToolBar: true,
            withPanelPaddingTop: true,
            withPanelPaddingBottom: true,
            withPanelHeader: true,
            withTablePagination: true,
          }),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        pageInfo={{ page: current_page, total }}
        data={content_list}
        columns={[
          {
            columnProps: {
              flexGrow: 1,
            },
            header: '预约时间',
            cell: <Cell dataKey="number" />,
          },
          {
            columnProps: {
              flexGrow: 1,
            },
            header: '客户名',
            cell: <UserNameCell dataKey="user_name" />,
          },
          {
            columnProps: {
              flexGrow: 1,
            },
            header: '手机尾号',
            cell: <Cell dataKey="phone" />,
          },
          {
            columnProps: {
              flexGrow: 1,
            },
            header: '预约车牌号',
            cell: <Cell dataKey="content" />,
          },
          {
            columnProps: {
              flexGrow: 1,
            },
            header: '备注',
            cell: <Cell dataKey="remark" />,
          },
          {
            columnProps: {
              flexGrow: 1,
            },
            header: '入场时间',
            cell: (
              <Cell>
                {(rowData) => {
                  return rowData?.write_off_time ? (
                    <div className="flex">
                      <span>{rowData.write_off_time}</span>
                      <Button
                        appearance="link"
                        onClick={async () => {
                          const { status } = await fetchRevokeIn(_.pick(rowData, 'id'));
                          if (status === SERVICE_STATUS.SUCCESS) {
                            Alert.success('撤销成功');
                            onRefresh();
                          }
                        }}
                        style={{ color: '#73D8DA', marginLeft: 5 }}
                      >
                        撤销
                      </Button>
                    </div>
                  ) : (
                    <Button
                      appearance="link"
                      onClick={async () => {
                        const { status } = await fetchCheckIn(_.pick(rowData, 'id'));
                        if (status === SERVICE_STATUS.SUCCESS) {
                          Alert.success('入场成功');
                          onRefresh();
                        }
                      }}
                    >
                      确认入场
                    </Button>
                  );
                }}
              </Cell>
            ),
          },
          {
            columnProps: {
              flexGrow: 1,
            },
            header: '出场时间',
            cell: <Cell dataKey="check_out_time" />,
          },
        ]}
        onChange={handleTableChange}
      >
        <Table.Body
          height={useContentHeight({
            withToolBar: true,
            withPanelPaddingTop: true,
            withPanelPaddingBottom: true,
            withPanelHeader: true,
            withTablePagination: true,
          })}
        />
        <Table.Pagination />
      </Table>
    </Panel>
  );
};

export default Parking;
